import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';
import type { BaseTestRubricItem } from './types';

/**
 * Fetch test-rubric-items
 * @param params can be used to customise/limit the test-rubric-items retrieved
 * @returns the requested test-rubric-items
 */
const getTestRubricItems: GetMany<BaseTestRubricItem> = async (params) =>
  (
    await ASSIST_API.get('test-rubric-items', {
      params: requestParams({}, params)
    })
  ).data;

/**
 * Fetch test-rubric-item (singular)
 * @param params can be used to customise/limit the test-rubric-item retrieved
 * @returns a single test-rubric-item
 */
const getTestRubricItem: GetOne<BaseTestRubricItem> = async (id, params) =>
  (await ASSIST_API.get(`test-rubric-items/${id}`, { params })).data;

/**
 * Hook implementation to fetch test-rubric-items
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns test-rubric-items matching request
 */
export const useTestRubricItems: AssistQuery.UseMany<BaseTestRubricItem> = (params, options) =>
  useQueryAssist(['test-rubric-items', params], () => getTestRubricItems(params), options);

/**
 * Given an testRubricItemId fetch and return the test rubric item
 *  - only runs once testRubricItemId is provided
 *  - fetches the one testRubricItem
 *  - caches data
 * @param id testRubicItemId
 * @param params optional parameters to change requested data
 * @returns the matching testRubricItem
 */
export const useTestRubricItem: AssistQuery.UseOne<BaseTestRubricItem> = (id, params, options) =>
  useQueryAssist(['test-rubric-items', { id, ...params }], () => getTestRubricItem(id, params), {
    ...options,
    enabled: !!id
  });
